import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  TextField,
  Alert,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { Auth } from 'aws-amplify';
import { getTeacherStudents } from '../../services/userService';
import { assignModuleToStudents } from '../../services/lessonService';
import { useAuth } from '../../hooks/useAuth';

interface Student {
  user_id: string;
  name: string;
  email: string;
}

interface AssignLessonDialogProps {
  open: boolean;
  onClose: () => void;
  moduleId: string;
  moduleTitle: string;
  onAssignSuccess: () => void;
}

const AssignLessonDialog: React.FC<AssignLessonDialogProps> = ({
  open,
  onClose,
  moduleId,
  moduleTitle,
  onAssignSuccess,
}) => {
  const { user } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<string>('');
  const [instructions, setInstructions] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadStudents();
    }
  }, [open]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use 'me' to get the current teacher's students
      // The backend auth middleware will resolve this to the actual user
      console.log('Loading students for current teacher');
      const response = await getTeacherStudents('me');
      console.log('Students loaded:', response);
      setStudents(response.students || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedStudentIds(typeof value === 'string' ? value.split(',') : value);
  };

  const handleAssign = async () => {
    if (selectedStudentIds.length === 0) {
      setError('Please select at least one student');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await assignModuleToStudents(
        moduleId,
        selectedStudentIds,
        dueDate || undefined,
        instructions || undefined,
      );

      setSuccess(`Lesson assigned to ${selectedStudentIds.length} student(s) successfully!`);
      setTimeout(() => {
        onAssignSuccess();
        handleClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to assign lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStudentIds([]);
    setDueDate('');
    setInstructions('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  const getSelectedStudentNames = () => {
    return students
      .filter((student) => selectedStudentIds.includes(student.user_id))
      .map((student) => student.name);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Lesson: {moduleTitle}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading && students.length === 0 ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="students-label">Select Students</InputLabel>
              <Select
                labelId="students-label"
                id="students-select"
                multiple
                value={selectedStudentIds}
                onChange={handleStudentChange}
                label="Select Students"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {getSelectedStudentNames().map((name) => (
                      <Chip key={name} label={name} size="small" />
                    ))}
                  </Box>
                )}
              >
                {students.length === 0 ? (
                  <MenuItem disabled>No students available</MenuItem>
                ) : (
                  students.map((student) => (
                    <MenuItem key={student.user_id} value={student.user_id}>
                      {student.name} ({student.email})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Due Date (Optional)"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Instructions (Optional)"
              multiline
              rows={4}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Add any special instructions for students..."
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={loading || selectedStudentIds.length === 0}
        >
          {loading ? <CircularProgress size={24} /> : 'Assign Lesson'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignLessonDialog;
